import { inject, Injectable } from '@angular/core';
import { combineLatest, iif, map, Observable, of, shareReplay, switchMap } from 'rxjs';

import { filterNull } from '../rxjs/filter-null';
import { AccessTier } from '../models/access-tier';
import { State } from '../models/state';
import { ShortMatrix } from '../models/matrix-short';
import { SpecialMatrixType } from '../enums/special-matrix-type';

import { CurrentUserService } from './current-user.service';

/** Service for managing current user access tier data. */
@Injectable({ providedIn: 'root' })
export class CurrentUserAccessTierService {
	private readonly currentUserService = inject(CurrentUserService);

	/** User access tier. Admin user always has 'null'. */
	private readonly userAccessTier$ = this.createUserAccessTierStream();

	/** List of jurisdictions available to user. */
	public readonly jurisdictionsAvailableToUser$ = this.createUserAvailableJurisdictionsStream();

	private readonly matrixesAvailableToUser$ = this.createUserAvailableMatrixesStream();

	/** Whether user has access to Definitions matrix. */
	public readonly hasAccessToDefinitionsMatrix$ = this.createHasAccessToSpecialTypeMatrixStream(SpecialMatrixType.Definitions);

	/** Whether user has access to Due Diligence matrix. */
	public readonly hasAccessToDueDiligenceMatrix$ = this.createHasAccessToSpecialTypeMatrixStream(SpecialMatrixType.DueDiligence);

	/** Whether user has access to Reporting Mechanics matrix. */
	public readonly hasAccessToReportingMechanicsMatrix$ = this.createHasAccessToSpecialTypeMatrixStream(
		SpecialMatrixType.ReportingMechanics,
	);

	/** Whether user has access to Priority Rules. */
	public readonly hasAccessToPriorityRules$ = this.createHasAccessToRulesStream(
		accessTier => accessTier.hasAccessToPriorityRules,
	);

	/** Whether user has access to IRA Distribution. */
	public readonly hasAccessToIraDistribution$ = this.createHasAccessToRulesStream(
		accessTier => accessTier.hasAccessToIraDistribution,
	);

	/** Whether user has access to Reporting Mechanics page. */
	public readonly hasAccessToReportingMechanicsPage$ = combineLatest([
		this.hasAccessToReportingMechanicsMatrix$,
		this.hasAccessToDueDiligenceMatrix$,
	]).pipe(
		map(values => values.some(Boolean)),
	);

	/** All user access tier flags. */
	public readonly userAccessTierFlags$ = combineLatest({
		/* eslint-disable rxjs/finnish */
		hasAccessToDefinitionsMatrix: this.hasAccessToDefinitionsMatrix$,
		hasAccessToDueDiligenceMatrix: this.hasAccessToDueDiligenceMatrix$,
		hasAccessToPriorityRules: this.hasAccessToPriorityRules$,
		hasAccessToIraDistribution: this.hasAccessToIraDistribution$,
		hasAccessToReportingMechanicsMatrix: this.hasAccessToReportingMechanicsMatrix$,
		/* eslint-enable rxjs/finnish */
	});

	private createUserAccessTierStream(): Observable<AccessTier | null> {
		return this.currentUserService.currentUser$.pipe(
			filterNull(),
			map(user => user.appUserData?.currentAccessTier ?? user.appUserData?.accessTier ?? null),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	private createUserAvailableMatrixesStream(): Observable<readonly ShortMatrix[]> {
		return this.userAccessTier$.pipe(
			map(accessTier => accessTier?.matrixes ?? []),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	private createUserAvailableJurisdictionsStream(): Observable<readonly State[]> {
		return this.userAccessTier$.pipe(
			map(accessTier => accessTier?.jurisdictions ?? []),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	private createHasAccessToSpecialTypeMatrixStream(matrixType: SpecialMatrixType): Observable<boolean> {
		const isAdmin$ = this.currentUserService.isAdmin$.pipe(
			map(isAdmin => isAdmin),
		);

		// Empty array means that user has access to all matrixes.
		const hasAccessToSpecialTypeMatrix$ = this.matrixesAvailableToUser$.pipe(
			map(matrixes => matrixes.length === 0 || matrixes.some(matrix => matrix.specialType === matrixType)),
		);

		return isAdmin$.pipe(
			switchMap(isAdmin => iif(
				() => isAdmin,
				of(true),
				hasAccessToSpecialTypeMatrix$,
			)),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	private createHasAccessToRulesStream(getAccessFlagFn: (accessTier: AccessTier) => boolean): Observable<boolean> {
		const isAdmin$ = this.currentUserService.isAdmin$.pipe(
			map(isAdmin => isAdmin),
		);

		const hasAccessToRules$ = this.userAccessTier$.pipe(
			map(userAccessTier => userAccessTier == null ? false : getAccessFlagFn(userAccessTier)),
		);

		return isAdmin$.pipe(
			switchMap(isAdmin => iif(
				() => isAdmin,
				of(true),
				hasAccessToRules$,
			)),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}
}
